type Choices = ReadonlyArray<{ text: string }>;

export interface QuestionsAnswers
  extends Readonly<{
    questionText: string;
    openAnswerText: string;
    choices: Choices;
  }> {}

export const onlineRsvpPageData = {
  seo: {
    title: 'Free Online RSVP for Weddings (With Website + Invites) - Joy',
    content:
      'The easiest online wedding RSVP system. Create custom questions and RSVPs for multiple wedding events. Track responses in free guest list manager.',
  },
  speechBubbles: {
    eyebrow: 'Guest Messaging & Reminders',
    title: 'Poke the slow pokes.',
    content: 'Easily make the announcement, <br/> send a reminder, or get a message <br/> out to everyone in just a few taps.',
  },
  cx: {
    title: 'In case you need tech<br/>(or emotional) support',
    content:
      'Our teams are here for you day or night — our average response time<br/>is less than 3 hours and we’ll always go the extra mile.',
    quote: 'I get paid to help people in love — it doesn’t get better than that!',
    caption: 'Jane Doe, Team Member since 2015',
  },

  flexibleRSVPQuestions: {
    main: {
      eyebrow: 'Customizable RSVP Questions',
      title: 'Go beyond attendance questions.',
      content: 'Create custom RSVP questions to ask your guests about their meal selections, travel plans, and anything else.',
    },
    mealChoices: {
      eyebrow: 'Allergies & Meal Choices',
      title: 'Ask your guests what they’d like to eat, or need to avoid.',
      content:
        'Catering is no longer just “chicken or steak?” We’ve made it easy to get as detailed as you want, from allergies to appetizers, desserts to drinks.',
      imgAlt: 'meal choices',
    },
    accommodations: {
      eyebrow: 'Guest Accommodations',
      title: 'Know who is staying where, so you can better prepare.',
      content:
        'Plan hotel blocks better. Ask your guests whether they have a place to stay and whatever else they’ll need to attend your ceremony.',
      imgAlt: 'accommodations',
    },
  },

  allTogetherNow: {
    unified: {
      eyebrow: 'Multi-Event RSVP',
      title: 'All together now.',
      content: 'Track responses for every event right in your Guest List.',
    },
    households: {
      eyebrow: '',
      title: 'Easily group your guests.',
      content: 'Save time and stay organized with guest groups that allow you to manage plus ones, table arrangements, and more.',
    },
    rsvpGroups: {
      eyebrow: '',
      title: 'Households and parties can respond together.',
      content: 'Let guests RSVP on behalf of their party, whether they are family or a couple. ',
    },
  },

  allTogetherNowMobile: {
    unified: {
      eyebrow: 'Unified RSVP for Multiple Events',
      title: 'All together now.',
      content: `Everyone's responses for every event, so you can see all your guests at a glance.`,
    },
    households: {
      eyebrow: 'Housholds & Parties',
      title: 'Easily group your guests.',
      content:
        'Save time and stay organized with guest groups that allow you to control people`s plus ones. Even let parties and households RSVP together.',
    },
    rsvpGroups: {
      eyebrow: 'RSVP Groups',
      title: 'Households and parties can respond together.',
      content: 'Let guests RSVP on behalf of their party, whether they are family or a couple.',
    },
  },

  allTogetherTable: {
    header: {
      guest: 'Guest',
      rehearsal: 'Rehearsal',
      ceremony: 'Ceremony',
      reception: 'Reception',
      brunch: 'Brunch',
    },
    rows: [
      {
        name: 'Jameela',
        rehearsal: true,
        ceremony: true,
        reception: true,
        brunch: true,
        src: 'common/avatars/avatars_photo_avatar-37.jpg',
      },
      {
        name: 'Christopher',
        rehearsal: undefined,
        ceremony: undefined,
        reception: undefined,
        brunch: undefined,
        src: 'common/avatars/avatars_photo_avatar-5.jpg',
      },
      {
        name: 'Jordan',
        rehearsal: true,
        ceremony: true,
        reception: true,
        brunch: true,
        src: 'common/avatars/avatars_photo_avatar-24.jpg',
      },
      {
        name: 'Javier',
        rehearsal: false,
        ceremony: false,
        reception: true,
        brunch: true,
        src: 'common/avatars/avatars_photo_avatar-19.jpg',
      },
      {
        name: 'Samantha',
        rehearsal: true,
        ceremony: true,
        reception: true,
        brunch: false,
        src: 'common/avatars/avatars_photo_avatar-26.jpg',
      },
      {
        name: 'Eduardo',
        rehearsal: false,
        ceremony: false,
        reception: true,
        brunch: false,
        src: 'common/avatars/avatars_photo_avatar-21.jpg',
      },
    ],
  },

  answers: {
    eyebrow: 'Smart Follow-up Questions',
    title: 'Answers often lead to more questions. Ask them.',
  },

  backgroundPhoto: {
    eyebrow: 'The Stressless Guest List',
    title: 'Featuring everything a spreadsheet can’t do.',
  },

  knowWhoToExpect: {
    title: 'Know who and what to expect.',
    content: `Ask any RSVP question you can imagine to help you plan, and quickly view guests' responses as they come in.`,
    altText: 'event images',
  },
  events: {
    eyebrow: 'Multiple & Private Events',
    title: 'Odds are, your wedding is more than one event.',
    content:
      'That’s why we designed the most powerful RSVP to handle multiple events from the ground up, so you can do it all without the hassle.',
    learnMore: 'Learn More',
  },

  plusOnes: {
    eyebrow: 'Groups and Plus One Controls',
    title: 'Prepped for Plus Ones.',
    content: `Manage who gets a plus one (and who doesn't) for stress-free RSVPs.`,
    imageAltTextUpper: 'Assign Plus Ones in the Joy guest list',
    imageAltTextLower: 'Or let guests add plus ones during the RSVP process',
    altText: 'Plus one household',
    columns: [
      {
        title: 'Never wonder who your guests are bringing.',
        content: `Want to know exactly who's attending to prep your place cards? Allow guests to enter their plus one's name when RSVPing and it will update in your guest list.`,
      },
      {
        eyebrow: 'Open Plus Ones',
        title: 'Large venue? Large family? The more the merrier!',
        content: 'Open everyone’s plus ones at once, so your guests can bring their family and friends.',
      },
    ],
  },

  rsvpByName: {
    eyebrow: 'RSVP Name Matching',
    title: 'Limit RSVP to your guest list.',
    content: `If you so choose, you can require guests to enter their name for any event’s RSVP, avoiding potential awkwardness and keeping things nice and tidy.`,
  },

  unlockedPlusOnes: {
    title: 'Unlock Plus Ones for Larger Venues and Families',
    body: `Some weddings are large—like, really, really large. Unlock everyone’s plus ones at once, so your guests can bring more family and friends.`,
  },

  creativeQuestionsAndAnswers: {
    title: 'Go beyond<br/>&quot;are you attending?&quot;',
    body: 'Explore some of the common ways we’ve seen couples get the answers they need from guests.',
    icons: [
      {
        icon: 'heart',
        text: 'Matchmaking',
      },
      {
        icon: 'songRequest',
        text: 'Song Suggestions',
      },
      {
        icon: 'allergies',
        text: 'Dietary Restrictions',
      },
      {
        icon: 'translator',
        text: 'Translators Needed',
      },
      {
        icon: 'destinationWedding',
        text: 'Destination Wedding Perks',
      },
      {
        icon: 'wellWishes',
        text: 'Well Wishes & Advice',
      },
      {
        icon: 'rings',
        text: 'Picking Sides',
      },
    ],
    questionsAnswers: [
      {
        questionText: 'Are you single?',
        openAnswerText: '',
        choices: [
          {
            text: 'Yes',
          },
          {
            text: 'No',
          },
          {
            text: 'That’s none of your business!',
          },
        ] as Choices,
      } as QuestionsAnswers,
      {
        questionText: 'What track will get you out on the dance floor?',
        openAnswerText: 'Help us make the perfect playlist!',
        choices: [] as Choices,
      } as QuestionsAnswers,
      {
        questionText: 'Do you have any dietary restrictions?',
        openAnswerText: '',
        choices: [
          {
            text: 'None',
          },
          {
            text: 'Vegetarian',
          },
          {
            text: 'Vegan',
          },
          {
            text: 'Nut Allergy',
          },
          {
            text: 'Lactose Intolerant',
          },
          {
            text: 'Other',
          },
        ] as Choices,
      } as QuestionsAnswers,
      {
        questionText: 'Bạn có cần một người dịch tiếng Anh không?',
        openAnswerText: '',
        choices: [
          {
            text: 'Dạ',
          },
          {
            text: 'Không',
          },
        ] as Choices,
      } as QuestionsAnswers,
      {
        questionText: 'Are you bringing a special someone?',
        openAnswerText: '',
        choices: [
          {
            text: 'Yes',
          },
          {
            text: 'No',
          },
        ] as Choices,
      } as QuestionsAnswers,
      {
        questionText: 'Do you have any thoughts or advice to share with the happy couple?',
        openAnswerText: 'Give us all your wisdom!',
        choices: [] as Choices,
      } as QuestionsAnswers,
      {
        questionText: 'Who did you know first (or who do you know best)?',
        openAnswerText: '',
        choices: [
          {
            text: 'Leslie',
          },
          {
            text: 'Ben',
          },
        ] as Choices,
      } as QuestionsAnswers,
    ],
  },

  echoSection: {
    eyebrow: 'Environmentally-Friendly Invitations',
    title: 'Share your love with the planet.',
    content:
      'With a tree planted in your honor for every print order, your invitations  are planet positive. And with online invitations and your connected guest list, you’re saving more than time and money.',
  },

  rsvpTogether: {
    eyebrow: 'RSVP Groups',
    title: 'Households and parties easily RSVP together.',
    content:
      'Making sure you get RSVPs from the right people isn’t hard. Turn on exact name match and password protect your website to do so.',
  },

  rsvpTracking: {
    eyebrow: 'RSVP Tracking',
    title: 'The best weddings know what to expect.',
    content: 'Get an accurate count of your guests, including plus ones. Track RSVPs, meal choices, and more to plan your big day better.',
    outOfTowners: 'Out of towners',
    babies: 'Babies',
    collegeFriends: 'College friends',
    bestFriends: 'Best friends',
    peoplePerTable: 'People per table',
    learnMore: 'Learn More',
  },

  runTheShow: {
    columns: [
      {
        eyebrow: 'Real-time RSVP Controls',
        title: 'Turn RSVP off (or on) at any time.',
      },
      {
        title: '',
        content:
          'Give your guests the flexibility to update their responses while still having the option to lock RSVP responses at any time.',
      },
    ],
  },

  saveTheDate: {
    eyebrow: 'Personalized Save the Dates',
    title: 'Customized to each person you invite, just like your site.',
  },

  testSendTrack: {
    columns: [
      {
        eyebrow: 'Streamlined Sending',
        title: 'Test, Send, Track.',
        content: 'It’s as easy as one, two, three.',
      },
      {
        title: 'Ready with a test email',
        content: 'Send yourself a sample RSVP to make sure your questions are how you want them to appear.',
        altText: 'easily organize your guest list',
      },
      {
        title: 'Ready with a test email',
        content: 'Send yourself a sample RSVP to make sure your questions are how you want them to appear.',
        altText: 'our guestlist is helpful',
      },
      {
        title: 'Ready with a test email',
        content: 'Send yourself a sample RSVP to make sure your questions are how you want them to appear.',
        altText: 'easily track guests',
      },
      {
        prevNumber: '3',
        number: '4',
        updateNumber: '5',
        eyebrow: 'RSVP Notifications',
        title: 'Wherever you go, never miss a response.',
        content: 'Get real-time RSVP updates in your inbox.',
      },
    ],
  },
  heroSection: {
    title: 'Powerful RSVP that brings the dance to attendance.',
    content: 'Free online RSVP paired with your beautiful wedding website is just the beginning of this perfect match.',
    getStartedBtn: 'Get Started',
  },
  questionBuilder: {
    firstSection: {
      eyebrow: 'Question Builder',
      title: 'Ask your guests anything you want',
      details: 'Choose from different question formats to ask (and receive) the way you want.',
      attendance: { title: 'Attendance', body: 'You’ll need to know whose going to be there. We’ve made it easy to do so.' },
      multipleChoice: { title: 'Multiple Choice', body: 'Perfect for meals, figuring out whose side the guest is from, and more.' },
      shortAnswer: { title: 'Short Answer', body: 'Get song suggestions or well wishes with open-ended questions.' },
      questionsMenu: {
        title: 'Save time with Question Suggestions',
        body: 'Having helped with thousands of weddings, we’ve&nbsp;compiled the greatest hits to get you started.',
        questions: [
          {
            alt: 'Attendance',
            cardTagAlt: 'RSVP Attendance',
            cardQuestionCopy: '',
          },
          {
            alt: 'Address',
            cardTagAlt: 'RSVP Address',
            cardQuestionCopy: 'Will you be attending our wedding?',
          },
          {
            alt: 'Phone',
            cardTagAlt: 'RSVP Phone',
            cardQuestionCopy: 'Can you provide a cell phone number where your party may be reached?',
          },
          {
            alt: 'Accomodations',
            cardTagAlt: 'RSVP Accomodations',
            cardQuestionCopy: 'Do you need help finding hotel accommodations?',
          },
          {
            alt: 'Song request',
            cardTagAlt: 'RSVP Song request',
            cardQuestionCopy: 'What song will get you out on the dance floor?',
          },
          {
            alt: 'Well wishes',
            cardTagAlt: 'RSVP Well wishes',
            cardQuestionCopy: 'Just for fun, what’s the best piece of marriage advice you’ve heard?',
          },
          {
            alt: 'Dietary restrictions',
            cardTagAlt: 'RSVP Dietary restrictions',
            cardQuestionCopy: 'What entree would you prefer at our wedding?',
          },
          {
            alt: 'Wedding destination',
            cardTagAlt: 'RSVP Wedding destination',
            cardQuestionCopy: 'Just for fun, where should we go on our honeymoon?',
          },
        ],
      },
    },
    secondSection: {
      eyebrow: 'Customizable Questions',
      title: 'Give your questions some personality',
      details: 'This is your big day. Make every detail yours — add some emoji, sass, or a pinch of pizazz.',
    },
    thirdSection: {
      eyebrow: 'Private Questions',
      title: 'Choose who gets asked what',
      details: 'Sometimes you need to ask the 12 people who need to know, not your entire guest list.',
    },
  },
  statsSection: {
    title: 'We’ve helped millions celebrate love around the world.',
    subtitle: 'That’s more cake, flowers and happy tears than we can count. ',
  },
  guestStats: [
    {
      title: 'million',
      body: 'The largest guest list in Joy',
      endingNumber: 8,
      incrementFrequency: 150,
    },
    {
      title: 'countries',
      body: 'Screaming babies have interrupted weddings on',
      endingNumber: 86,
      incrementFrequency: 100,
    },
    {
      title: 'guests',
      body: 'People around the world',
      endingNumber: 768,
      incrementFrequency: 20,
    },
  ],
  personalSaveTheDatesSectionData: {
    templateAltText1: 'Save the Date template',
    templateAltText2: 'Save the Date template 2',
    main: {
      eyebrow: 'Personalized Save the Dates',
      title: 'Haven’t sent out Save the Date cards? We’ve got you.',
      content: '',
    },
    ctaText: 'Browse Save the Dates',
  },
  connectedGuestList: {
    eyebrow: 'The Connected Guest List',
    title: 'The powerhouse behind RSVP: your Guest List.',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
  },
  smartRSVPInviteFeatureSection: {
    mainTextTitle: 'Smart RSVP. Inviting features.',
    mainTextBody: 'The most powerful RSVP on the planet tracks all your details so you can focus on the fun.',
    featureList: [
      {
        image: 'online-rsvp/icon-multiple-rsvp.svg',
        title: 'Multi-Event RSVPs',
        body: 'Track attendance for all your wedding events easily in one place.',
      },
      {
        image: 'online-rsvp/icon-grouping.svg',
        title: 'Easy Guest Grouping',
        body: 'Simplify the RSVP process for your guests and their plus ones.',
      },
      {
        image: 'online-rsvp/icon-custom-q.svg',
        title: 'Custom RSVP Questions',
        body: 'Ask anything. From meal choices to song requests, it’s that simple.',
      },
      {
        image: 'online-rsvp/icon-tracking.svg',
        title: 'Track Responses',
        body: 'With every response right in your guest list, watch the RSVPs roll in.',
      },
    ],
  },
} as const;
